import usePrayContext from 'hooks/usePrayContext';
import React, { useEffect, useState } from 'react';

import MessageModal from '@pray/shared/components/MessageModal/MessageModal';
import { login } from '@pray/shared/utils/auth';
import logger from '@pray/shared/utils/logger';
import { navigate } from '@pray/shared/utils/navigation';
import { parseQueryString, removeQueryParamsFromUrl } from '@pray/shared/utils/parseFromQuery';
import storage from '@pray/shared/utils/storage';

/**
 * MagicLogin component handles magic link and token-based authentication flows.
 * It processes URL query parameters for magic links and tokens, manages login state,
 * and displays appropriate feedback messages to users.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} [props.isSubscribeFlow=false] - Whether the login is part of a subscribe flow
 * @param {Function} [props.handleSuccessfulLogin] - Callback fired after successful login
 * @param {Function} [props.handleSuccessfulTokenLogin] - Callback fired after successful token login
 * @param {Function} [props.onTokenLoginFailed] - Callback fired when token login fails
 * @param {Function} [props.onMessageModalClosed] - Callback fired when message modal is closed
 * @param {Function} [props.onMagicLoginChecked] - Callback fired when magic login check completes
 * @returns {JSX.Element | null} MessageModal component or null
 */
const MagicLogin = ({
  isSubscribeFlow = false,
  handleSuccessfulLogin,
  handleSuccessfulTokenLogin,
  onTokenLoginFailed,
  onMessageModalClosed,
  onMagicLoginChecked,
}) => {
  const prayContext = usePrayContext();
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [messageModalMessage, setMessageModalMessage] = useState('');

  const checkForMagicLinkLoginQueryParams = async () => {
    const query = parseQueryString();
    const magicEmail = query.email;
    const magicCountryCode = query.country_code;
    const magicPhone = query.phone;
    const magicCode = query.code;
    const magicLoginToken = query.mlt || query.magic_login_token;

    const isInfoForCodeAvailable = !!(magicEmail || (magicPhone && magicCountryCode));
    const isCodeAndInfoAvailable = !!(magicCode && isInfoForCodeAvailable);
    const isMagicLoginTokenAvailable = !!magicLoginToken;

    if (isMagicLoginTokenAvailable) {
      await _tokenMagicLogin(magicLoginToken);
      return;
    }

    if (isCodeAndInfoAvailable) {
      const form = {
        email: magicEmail || null,
        country_code: magicCountryCode || null,
        phone: magicPhone || null,
        code: magicCode || null,
      };

      try {
        const errorMessage = await login(form);

        let modalMessage = 'You have been successfully logged in';

        if (errorMessage === null) {
          const user = storage.getUser();
          // update user in prayContext
          prayContext.setCurrentUser(user);
          // redirect subscribed users to Discover page in subscribe flows
          if (isSubscribeFlow && user.is_subscribed) {
            navigate('/discover/');
            return;
          }
          removeMagicLinkParamsFromUrl();
          // call success handler function if present
          setTimeout(() => handleSuccessfulLogin?.());
        } else if (errorMessage === 'magicLinkExpired') {
          modalMessage =
            'Your login link expired. Please click the new link in the email we just sent you to successfully log in';
        } else {
          modalMessage = 'Login to web failed, please try again';
        }
        setIsMessageModalOpen(true);
        setMessageModalMessage(modalMessage);
      } catch (error) {
        setIsMessageModalOpen(true);
        setMessageModalMessage('Login to web failed, please try again');
        logger.debug('Magic Login error', error);
      }
    }
  };

  const removeMagicLinkParamsFromUrl = () => {
    removeQueryParamsFromUrl({
      only: [
        'email',
        'code',
        'country_code',
        'phone',
        'mlt',
        'magic_login_token',
        '$web_only',
        '_branch_match_id',
        '_branch_referrer',
      ],
    });
  };

  async function _tokenMagicLogin(magicLoginToken) {
    try {
      const form = {
        magic_login_token: magicLoginToken,
      };

      // TODO: Add warning/notification when magic login switches from one logged-in user to another
      // This could happen if User A clicks a subscription management link meant for User B
      // Consider checking storage.getUser() before login() and showing a modal like:
      // "You've been logged in as [email]. Continue to manage subscription?"
      await login(form);

      if (handleSuccessfulTokenLogin) {
        handleSuccessfulTokenLogin();
      } else if (handleSuccessfulLogin) {
        handleSuccessfulLogin();
      }
      removeMagicLinkParamsFromUrl();
    } catch (error) {
      logger.debug('magic token login error', error);
      removeMagicLinkParamsFromUrl();
      setTimeout(() => onTokenLoginFailed?.());
    }
  }

  const handleCloseMessageModal = () => {
    onMessageModalClosed?.();
    setIsMessageModalOpen(false);
    removeMagicLinkParamsFromUrl();
  };

  useEffect(() => {
    checkForMagicLinkLoginQueryParams().finally(() => {
      onMagicLoginChecked?.();
    });
  }, []);

  if (!isMessageModalOpen) return null;

  return <MessageModal message={messageModalMessage} closeModal={handleCloseMessageModal} />;
};

export default MagicLogin;
